import { getVerifyState } from '../../lib/verify-states.js';

/**
 * The branded verify result card.
 *
 * This is the core visual output of TocynID. Used in two places:
 *   1. Branding page — live-updated preview as admin edits settings
 *   2. Public verify page — real response from /api/verify/:tenantSlug/:staffSlug
 *
 * Same component, different data sources. The shape passed in matches
 * the /api/verify response shape:
 *   { result, tenant: { name, logo_url, brand_color, tagline, phone, website },
 *     staff: { display_name, role, photo_url } | null }
 *
 * The `result` field drives the status badge + tagline + border colour.
 * The tenant's brand_color becomes the accent colour for the status
 * badge when status is "verified" — giving each tenant a recognisable,
 * branded positive state. Non-verified states use standard colours
 * (amber / red / grey) for safety — customers need consistent visual
 * cues for warning states regardless of tenant branding.
 */
export function VerifyPreviewCard({ data, density = 'default' }) {
  const result = data?.result || 'unknown';
  const state = getVerifyState(result);
  const tenant = data?.tenant;
  const staff = data?.staff;

  const verifiedAccent = tenant?.brand_color || 'var(--tocyn-green)';
  const badgeBg = result === 'verified' ? tintFromHex(verifiedAccent) : state.background;
  const badgeFg = result === 'verified' ? darkenFromHex(verifiedAccent) : state.color;

  return (
    <div className="verify-card" role="region" aria-label="Verification result">
      {/* Header — tenant branding */}
      <div className="verify-header">
        {tenant?.logo_url && (
          <img className="verify-tenant-logo" src={tenant.logo_url} alt={tenant.name || 'Organisation logo'} />
        )}
        {tenant?.name && (
          <div className="verify-tenant-name">{tenant.name}</div>
        )}
        {tenant?.tagline && (
          <p className="verify-tenant-tagline">{tenant.tagline}</p>
        )}
      </div>

      {/* Body — status + staff */}
      <div className="verify-body">
        <div
          className="verify-status-badge"
          style={{ background: badgeBg, color: badgeFg }}
        >
          {state.label}
        </div>

        {/* Show staff details for verified + under_review + no_longer_employed
            (i.e. everything except 'unknown' where staff is null) */}
        {staff ? (
          <>
            <div className="verify-photo-wrap">
              {staff.photo_url ? (
                <img src={staff.photo_url} alt="" />
              ) : (
                <div className="verify-photo-placeholder">
                  {(staff.display_name || '')
                    .split(/\s+/)
                    .map(p => p.charAt(0))
                    .filter(Boolean)
                    .slice(0, 2)
                    .join('') || '—'}
                </div>
              )}
            </div>
            <h2 className="verify-staff-name">{staff.display_name || '—'}</h2>
            {staff.role && <p className="verify-staff-role">{staff.role}</p>}
          </>
        ) : (
          <p className="verify-staff-role" style={{ margin: 'var(--sp-5) 0' }}>
            No staff record matched this code.
          </p>
        )}

        <p
          className="verify-tagline"
          style={{
            background: state.background,
            color: state.color,
            borderColor: state.border,
          }}
        >
          {state.tagline}
        </p>
      </div>

      {/* Footer — tenant contact */}
      {(tenant?.phone || tenant?.website) && (
        <div className="verify-footer">
          {tenant.phone && (
            <>
              <a href={`tel:${tenant.phone.replace(/\s/g, '')}`}>{tenant.phone}</a>
            </>
          )}
          {tenant.phone && tenant.website && ' · '}
          {tenant.website && (
            <a
              href={normaliseUrl(tenant.website)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {stripProtocol(tenant.website)}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────── */

function normaliseUrl(url) {
  if (!url) return '#';
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

function stripProtocol(url) {
  if (!url) return '';
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

/**
 * Lighten a hex colour to ~15% opacity for badge backgrounds.
 * If input isn't a valid 7-char hex, returns the unknown-state tint.
 */
function tintFromHex(hex) {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return 'var(--tocyn-green-l)';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, 0.15)`;
}

/**
 * Slightly darken a hex colour for badge text against the tint.
 * Approximates a 20% luminance drop by compressing each channel toward 0.
 */
function darkenFromHex(hex) {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return 'var(--tocyn-green-d)';
  const r = Math.round(parseInt(hex.slice(1, 3), 16) * 0.7);
  const g = Math.round(parseInt(hex.slice(3, 5), 16) * 0.7);
  const b = Math.round(parseInt(hex.slice(5, 7), 16) * 0.7);
  return `rgb(${r}, ${g}, ${b})`;
}
